// src/pages/PublicHousingIntakePage.js
// UNPUBLISHED (unlinked) public housing utility allowance INTAKE.
// One form — authority, development, metering ("who pays"), equipment
// fuels — and Generate instantly computes a draft 24 CFR 965-style
// per-development schedule via the compliance service's phEngine
// (POST /api/utility-allowance/public-housing/preview). No persistence;
// every generated schedule is draft + assumed until verified.
//
// Route: /utility-allowances/public-housing — not linked from anywhere.

import React, { useState } from 'react';
import Brand from '../components/Brand';

const COMPLIANCE_API =
  process.env.REACT_APP_COMPLIANCE_API ||
  (window.location.hostname === 'localhost'
    ? 'http://localhost:3200'
    : 'https://housing-compliance-service.onrender.com');

const T = {
  blue600: '#1d4ed8', // blue-700: reads at 4.5:1 on the blue-100 chips (WCAG 1.4.3),
  blue100: '#dbeafe',
  gray900: '#111827',
  gray600: '#4b5563',
  gray500: '#5f6673', // darkened for 4.5:1 on light backgrounds (WCAG 1.4.3),
  gray400: '#6b7280', // darkened for 4.5:1 on white (WCAG 1.4.3),
  gray200: '#e5e7eb',
  gray100: '#f3f4f6',
  white: '#ffffff',
  radius: '16px',
  radiusSm: '12px',
  radiusPill: '999px',
  shadowSm: '0 4px 6px -1px rgba(0,0,0,0.07), 0 2px 4px -2px rgba(0,0,0,0.05)',
  gradient: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 50%, #faf5ff 100%)',
  font: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const ORGS = [
  { slug: 'salem', label: 'Salem Housing Authority' },
  { slug: 'waltham', label: 'Waltham Housing Authority' },
  { slug: 'winchester', label: 'Winchester Housing Authority' },
  { slug: 'arlington', label: 'Arlington Housing Authority' },
  { slug: 'concord', label: 'Concord Housing Authority' },
];

const STRUCTURES = [
  { idx: 1, label: 'Manufactured Home (Mobile Home)' },
  { idx: 2, label: 'Single Family Detached (House)' },
  { idx: 3, label: 'Single Family Attached (Duplex/Rowhouse/Townhouse)' },
  { idx: 4, label: 'Low-Rise Apt (2-4 unit Walk-up)' },
  { idx: 5, label: 'High-Rise Apt (5+ unit Bldg)' },
];

const SERVICES = [
  { key: 'heating', label: 'Heating' },
  { key: 'waterHeating', label: 'Water Heating' },
  { key: 'cooking', label: 'Cooking' },
  { key: 'otherElectric', label: 'Other Electric (lights, plugs)' },
  { key: 'ac', label: 'Air Conditioning' },
  { key: 'waterSewer', label: 'Water & Sewer' },
  { key: 'trash', label: 'Trash Collection' },
];

const FUELS = {
  heatingFuel: { label: 'Heating fuel', when: 'heating', options: [
    ['gas', 'Natural Gas'], ['electric', 'Electric Resistance'], ['heatpump', 'Electric Heat Pump'], ['oil', 'Fuel Oil'], ['propane', 'Bottle Gas (Propane)'],
  ]},
  waterHeatFuel: { label: 'Water heating fuel', when: 'waterHeating', options: [
    ['gas', 'Natural Gas'], ['electric', 'Electric'], ['oil', 'Fuel Oil'], ['propane', 'Bottle Gas (Propane)'],
  ]},
  cookingFuel: { label: 'Cooking fuel', when: 'cooking', options: [
    ['gas', 'Natural Gas'], ['electric', 'Electric'], ['propane', 'Bottle Gas (Propane)'],
  ]},
};

const BED_CHOICES = [0, 1, 2, 3, 4, 5];

const label = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: T.gray600, marginBottom: '0.3rem' };
const input = {
  width: '100%', padding: '0.55rem 0.75rem', fontSize: '0.9rem', color: T.gray900,
  border: `1px solid ${T.gray200}`, borderRadius: T.radiusSm, background: T.white, outline: 'none', boxSizing: 'border-box',
};

function Chip({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick} style={{
      padding: '0.35rem 0.8rem', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
      borderRadius: T.radiusPill, border: `1px solid ${active ? T.blue600 : T.gray200}`,
      background: active ? T.blue100 : T.white, color: active ? T.blue600 : T.gray600,
    }}>{children}</button>
  );
}

export default function PublicHousingIntakePage() {
  const [form, setForm] = useState({
    org: '', name: '', address: '', program: '',
    units: '', structureIdx: '', bedrooms: [],
    metering: { heating: '', waterHeating: '', cooking: '', otherElectric: '', ac: '', waterSewer: '', trash: '' },
    equipment: { heatingFuel: '', waterHeatFuel: '', cookingFuel: '' },
  });
  const [schedule, setSchedule] = useState(null);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setMeter = (key, val) => setForm((f) => ({ ...f, metering: { ...f.metering, [key]: val } }));
  const setFuel = (key, val) => setForm((f) => ({ ...f, equipment: { ...f.equipment, [key]: val } }));
  const toggleBed = (b) => setForm((f) => ({
    ...f,
    bedrooms: f.bedrooms.includes(b) ? f.bedrooms.filter((x) => x !== b) : [...f.bedrooms, b].sort((a, z) => a - z),
  }));

  const generate = async () => {
    setError(null);
    if (!form.org) { setError('Pick the housing authority.'); return; }
    if (!form.name.trim()) { setError('Enter the development name.'); return; }
    if (!form.program) { setError('Pick the program.'); return; }
    if (!form.structureIdx) { setError('Pick the building type.'); return; }
    if (!form.bedrooms.length) { setError('Pick at least one bedroom size.'); return; }
    const unanswered = SERVICES.filter((s) => !form.metering[s.key]);
    if (unanswered.length) { setError(`Answer who pays for: ${unanswered.map((s) => s.label).join(', ')}.`); return; }
    const missingFuels = Object.entries(FUELS)
      .filter(([key, f]) => form.metering[f.when] === 'tenant' && !form.equipment[key]);
    if (missingFuels.length) {
      const names = missingFuels.map(([, f]) => f.label.toLowerCase());
      const list = names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0];
      setError(`Pick the ${list}.`);
      return;
    }
    setGenerating(true);
    setSchedule(null);
    try {
      const r = await fetch(`${COMPLIANCE_API}/api/utility-allowance/public-housing/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          org: form.org, name: form.name.trim(), address: form.address.trim() || undefined,
          program: form.program, units: form.units ? Number(form.units) : undefined,
          structureIdx: form.structureIdx, bedrooms: form.bedrooms,
          metering: form.metering, equipment: form.equipment,
        }),
      });
      const body = await r.json();
      if (!r.ok || !body.schedule) throw new Error(body.message || 'Could not generate the schedule.');
      setSchedule(body.schedule);
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const section = { background: T.white, border: `1px solid ${T.gray200}`, borderRadius: T.radius, boxShadow: T.shadowSm, padding: '1.5rem', marginBottom: '1.25rem' };
  const h2 = { fontSize: '0.95rem', fontWeight: 700, color: T.gray900, margin: '0 0 1rem' };

  return (
    <div style={{ fontFamily: T.font, minHeight: '100vh', background: T.gradient, display: 'flex', flexDirection: 'column' }}>
      <header style={{ background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', borderBottom: `1px solid ${T.gray200}`, padding: '1.25rem 1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', maxWidth: '1400px', margin: '0 auto', flexWrap: 'wrap' }}>
          <Brand as="a" href="https://gethousingai.com" theme="light" markClassName="text-blue-600"
            style={{ fontWeight: 700, color: T.gray900, fontSize: '1.35rem', textDecoration: 'none' }} />
          <span style={{ color: '#d1d5db', fontSize: '1.5rem', fontWeight: 300 }}>|</span>
          <span style={{ fontSize: '0.85rem', padding: '0.25rem 0.75rem', background: T.blue100, color: T.blue600, borderRadius: T.radiusPill, fontWeight: 600 }}>
            Public Housing Utility Allowance — Intake
          </span>
        </div>
      </header>

      <main style={{ padding: '2.5rem 1rem', flex: 1 }}>
        <div style={{ maxWidth: '860px', margin: '0 auto' }}>
          <h1 style={{ fontSize: '1.4rem', fontWeight: 800, color: T.gray900, margin: '0 0 0.35rem', letterSpacing: '-0.02em' }}>
            Generate a Development Utility Allowance
          </h1>
          <p style={{ color: T.gray600, fontSize: '0.9rem', margin: '0 0 1.5rem', lineHeight: 1.6 }}>
            Answer what the authority already knows — who pays for what, and what&rsquo;s installed —
            and the schedule computes instantly from current cited utility rates
            (24 CFR 965 Subpart E / 760 CMR 6.04 methodology, engineering-estimate consumption).
          </p>

          {/* 1 — Authority + development */}
          <div style={section}>
            <h2 style={h2}>1 &middot; Development</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem' }}>
              <div>
                <label htmlFor="ph-housing-authority-rates-on-file" style={label}>Housing authority (rates on file)</label>
                <select id="ph-housing-authority-rates-on-file" style={input} value={form.org} onChange={(e) => set({ org: e.target.value })}>
                  <option value="" disabled>Select an authority…</option>
                  {ORGS.map((o) => <option key={o.slug} value={o.slug}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="ph-development-name" style={label}>Development name</label>
                <input id="ph-development-name" style={input} value={form.name} placeholder="e.g. Rainbow Terrace" onChange={(e) => set({ name: e.target.value })} />
              </div>
              <div>
                <label htmlFor="ph-address-optional" style={label}>Address (optional)</label>
                <input id="ph-address-optional" style={input} value={form.address} placeholder="Street, City" onChange={(e) => set({ address: e.target.value })} />
              </div>
              <div>
                <label htmlFor="ph-program" style={label}>Program</label>
                <select id="ph-program" style={input} value={form.program} onChange={(e) => set({ program: e.target.value })}>
                  <option value="" disabled>Select a program…</option>
                  <option>State Public Housing — Elderly/Disabled</option>
                  <option>State Public Housing — Family</option>
                  <option>Federal Public Housing — Elderly/Disabled</option>
                  <option>Federal Public Housing — Family</option>
                </select>
              </div>
              <div>
                <label htmlFor="ph-total-units-optional" style={label}>Total units (optional)</label>
                <input id="ph-total-units-optional" style={input} type="number" min="1" value={form.units} onChange={(e) => set({ units: e.target.value })} />
              </div>
              <div>
                <label htmlFor="ph-building-type" style={label}>Building type</label>
                <select id="ph-building-type" style={input} value={form.structureIdx} onChange={(e) => set({ structureIdx: Number(e.target.value) })}>
                  <option value="" disabled>Select a building type…</option>
                  {STRUCTURES.map((s) => <option key={s.idx} value={s.idx}>{s.label}</option>)}
                </select>
              </div>
            </div>
            <div style={{ marginTop: '1rem' }}>
              <label htmlFor="ph-bedroom-sizes-in-this-development-bed-ch" style={label}>Bedroom sizes in this development</label>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {BED_CHOICES.map((b) => (
                  <Chip key={b} active={form.bedrooms.includes(b)} onClick={() => toggleBed(b)}>
                    {b === 0 ? 'Studio' : `${b} BR`}
                  </Chip>
                ))}
              </div>
            </div>
          </div>

          {/* 2 — Metering */}
          <div style={section}>
            <h2 style={h2}>2 &middot; Who pays for each service?</h2>
            <p style={{ fontSize: '0.8rem', color: T.gray500, margin: '-0.5rem 0 1rem' }}>
              Allowances exist only for services the tenant pays directly. Authority-paid (master-metered) services carry no allowance.
            </p>
            {SERVICES.map((s) => (
              <div key={s.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', padding: '0.5rem 0', borderTop: `1px solid ${T.gray100}`, flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.88rem', color: T.gray900 }}>{s.label}</span>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <Chip active={form.metering[s.key] === 'tenant'} onClick={() => setMeter(s.key, 'tenant')}>Tenant pays</Chip>
                  <Chip active={form.metering[s.key] === 'authority'} onClick={() => setMeter(s.key, 'authority')}>Authority pays</Chip>
                </div>
              </div>
            ))}
          </div>

          {/* 3 — Equipment fuels (only for tenant-paid services that need one) */}
          <div style={section}>
            <h2 style={h2}>3 &middot; Equipment</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem' }}>
              {Object.entries(FUELS).map(([key, f]) => (
                form.metering[f.when] === 'tenant' ? (
                  <div key={key}>
                    <label style={label}>{f.label}</label>
                    <select id="ph-bedroom-sizes-in-this-development-bed-ch" style={input} value={form.equipment[key]} onChange={(e) => setFuel(key, e.target.value)}>
                      <option value="" disabled>Select a fuel…</option>
                      {f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                ) : null
              ))}
            </div>
            {['heating', 'waterHeating', 'cooking'].every((k) => form.metering[k] === 'authority') ? (
              <p style={{ fontSize: '0.82rem', color: T.gray500, margin: 0 }}>
                No fuel details needed — heating, water heating, and cooking are all authority-paid.
              </p>
            ) : ['heating', 'waterHeating', 'cooking'].every((k) => form.metering[k] !== 'tenant') && (
              <p style={{ fontSize: '0.82rem', color: T.gray500, margin: 0 }}>
                Answer section 2 first — fuel questions appear for each service the tenant pays.
              </p>
            )}
          </div>

          {error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: T.radiusSm, padding: '0.75rem 1rem', fontSize: '0.85rem', marginBottom: '1rem' }}>
              {error}
            </div>
          )}

          <button type="button" onClick={generate} disabled={generating} style={{
            width: '100%', padding: '0.85rem', fontSize: '0.95rem', fontWeight: 700, cursor: generating ? 'wait' : 'pointer',
            background: T.blue600, color: T.white, border: 'none', borderRadius: T.radiusSm, marginBottom: '1.5rem',
            opacity: generating ? 0.7 : 1,
          }}>
            {generating ? 'Generating…' : 'Generate Utility Allowance Schedule'}
          </button>

          {/* Result */}
          {schedule && (
            <>
              <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: T.radiusSm, padding: '0.85rem 1.1rem', marginBottom: '1.25rem', fontSize: '0.88rem', color: '#92400e' }}>
                Draft — engineering estimates. All rows reflect the intake&rsquo;s metering and equipment answers,
                pending verification against the authority&rsquo;s records; not an adopted schedule.
              </div>
              <div style={section}>
                <h2 style={{ fontSize: '1.2rem', fontWeight: 800, color: T.gray900, margin: '0 0 0.25rem', letterSpacing: '-0.02em' }}>
                  Utility Allowances &mdash; {schedule.name}
                </h2>
                <p style={{ color: T.gray600, fontSize: '0.88rem', margin: '0 0 1rem' }}>
                  {schedule.orgName}{schedule.address ? ` · ${schedule.address}` : ''} &middot; {schedule.program} &middot; {schedule.regulation}
                </p>
                <div style={{ overflowX: 'auto', border: `1px solid ${T.gray200}`, borderRadius: T.radiusSm }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ background: T.gray100 }}>
                        <th style={{ textAlign: 'left', padding: '0.6rem 0.85rem', color: T.gray600, fontWeight: 600 }}>Tenant-Paid Utility</th>
                        {schedule.bedrooms.map((b) => (
                          <th key={b} style={{ textAlign: 'center', padding: '0.6rem 0.85rem', color: T.gray600, fontWeight: 600 }}>{b}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {schedule.rows.map((row) => (
                        <tr key={row.label} style={{ borderTop: `1px solid ${T.gray100}` }}>
                          <td style={{ padding: '0.55rem 0.85rem', color: T.gray900 }}>
                            {row.label}
                            {row.assumed && <span style={{ color: T.gray400, fontSize: '0.75rem' }}> (assumed)</span>}
                          </td>
                          {row.values.map((v, i) => (
                            <td key={i} style={{ textAlign: 'center', padding: '0.55rem 0.85rem', color: T.gray900, fontVariantNumeric: 'tabular-nums' }}>${v}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {schedule.phaPaid?.length > 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: T.gray600, marginBottom: '0.35rem' }}>No allowance &mdash; paid by the authority:</div>
                    <ul style={{ margin: 0, paddingLeft: '1.1rem', color: T.gray500, fontSize: '0.8rem', lineHeight: 1.6 }}>
                      {schedule.phaPaid.map((p) => <li key={p}>{p}</li>)}
                    </ul>
                  </div>
                )}

                {schedule.rates && Object.keys(schedule.rates).length > 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: T.gray600, marginBottom: '0.35rem' }}>Rates used:</div>
                    {Object.entries(schedule.rates).map(([k, r]) => (
                      <div key={k} style={{ fontSize: '0.78rem', color: T.gray500, lineHeight: 1.55 }}>
                        ${r.value}/{r.unit || 'unit'}{r.fixed ? ` + $${Number(r.fixed).toFixed(2)}/mo customer charge` : ''} &middot; {r.source}
                        {r.url && <> &middot; <a href={r.url} target="_blank" rel="noopener noreferrer" style={{ color: T.blue600 }}>source</a></>}
                      </div>
                    ))}
                  </div>
                )}

                {schedule.assumptions?.length > 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: T.gray600, marginBottom: '0.35rem' }}>Pending verification:</div>
                    <ul style={{ margin: 0, paddingLeft: '1.1rem', color: T.gray500, fontSize: '0.78rem', lineHeight: 1.6 }}>
                      {schedule.assumptions.map((a) => <li key={a}>{a}</li>)}
                    </ul>
                  </div>
                )}

                <p style={{ fontSize: '0.78rem', color: T.gray500, lineHeight: 1.55, margin: '1rem 0 0' }}>
                  {schedule.methodNote}
                </p>
              </div>
            </>
          )}
        </div>
      </main>

      <footer style={{ background: T.gray900, color: T.white, padding: '2rem 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
          <Brand theme="dark" markClassName="text-blue-600"
            style={{ fontWeight: 600, fontSize: '0.95rem' }} />
          <div style={{ fontSize: '0.85rem', color: '#d1d5db' }}>© 2026 HousingAI. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}
